import React, { createContext, useEffect, useState } from "react";
import { HEADERS } from "../constants/header";
import { STORAGE_KEYS } from "../constants/storageKeys";
import { Auth, Issuer, JwtCredentials } from "../types/auth.types";
import { authManager } from "../utils/authManager";

export const JwtContext = createContext<Auth | undefined>(undefined);

const setTokenWithHeaders = (token: string): JwtCredentials => {
  sessionStorage.setItem(STORAGE_KEYS.JWT_TOKEN, token);
  authManager.setHeaders({
    [HEADERS.X_AUTHENTICATION]: token,
    [HEADERS.X_PLATFORM]: "web"
  });
  return { token };
};

const loadAndInitToken = (): JwtCredentials | null => {
  const token =
    window.authToken || sessionStorage.getItem(STORAGE_KEYS.JWT_TOKEN);
  if (token) {
    console.log(
      `[JwtContext] Loading token from ${window.authToken ? "window.authToken" : "sessionStorage"}`
    );
    return setTokenWithHeaders(token);
  }
  console.log("[JwtContext] No token found");
  return null;
};

export const JwtProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [jwtToken, setJwtToken] = useState<JwtCredentials | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    console.log("[JwtContext] Initializing JWT authentication");

    // NOTE: Always wait 100ms to show loader and handle race condition with injected script
    const timeoutId = setTimeout(() => {
      const token = loadAndInitToken();
      console.log(
        `[JwtContext] ${token ? "Token loaded successfully" : "User not authenticated"}`
      );
      setJwtToken(token);
      setIsLoading(false);
    }, 100);

    const handleMessage = (event: MessageEvent) => {
      const { authToken } = event.data;
      if (authToken) {
        console.log("[JwtContext] Received new authToken via postMessage");
        window.authToken = authToken;
        setJwtToken(setTokenWithHeaders(authToken));
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const value: Auth = {
    issuer: Issuer.JWT,
    isAuthenticated: !!jwtToken,
    isLoading,
    credentials: jwtToken
  };

  return <JwtContext.Provider value={value}>{children}</JwtContext.Provider>;
};
